/**
 * Utility functions for extracting EXIF data from images
 */

export interface ExifData {
    dateTime?: string;
    dateTimeOriginal?: string;
    dateTimeDigitized?: string;
    gpsLatitude?: number;
    gpsLongitude?: number;
    make?: string;
    model?: string;
    software?: string;
    orientation?: number;
}

/**
 * Extracts EXIF data from an image file
 */
export const extractExifData = async (file: File): Promise<ExifData> => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            const arrayBuffer = e.target?.result as ArrayBuffer;
            if (!arrayBuffer) {
                resolve({});
                return;
            }
            
            try {
                const exifData = parseExifData(arrayBuffer);
                resolve(exifData);
            } catch (error) {
                console.warn('Failed to parse EXIF data:', error);
                resolve({});
            }
        };
        
        reader.onerror = () => {
            resolve({});
        };
        
        reader.readAsArrayBuffer(file);
    });
};

/**
 * Alternative method to extract EXIF data using EXIF.js approach
 * This is more compatible with HEIC files
 */
export const extractExifDataAlternative = async (file: File): Promise<ExifData> => {
    return new Promise((resolve) => {
        const reader = new FileReader();
        
        reader.onload = (e) => {
            const arrayBuffer = e.target?.result as ArrayBuffer;
            if (!arrayBuffer) {
                resolve({});
                return;
            }
            
            try {
                // Try to find EXIF data using a more flexible approach
                const exifData = findExifDataInBuffer(arrayBuffer);
                resolve(exifData);
            } catch (error) {
                console.warn('Failed to parse EXIF data with alternative method:', error);
                resolve({});
            }
        };
        
        reader.onerror = () => {
            resolve({});
        };
        
        reader.readAsArrayBuffer(file);
    });
};

/**
 * More flexible EXIF data finder that works with various formats
 */
const findExifDataInBuffer = (arrayBuffer: ArrayBuffer): ExifData => {
    const dataView = new DataView(arrayBuffer);
    const exifData: ExifData = {};
    
    // Look for EXIF markers in the buffer
    const searchPatterns = [
        // JPEG EXIF marker
        { pattern: [0xFF, 0xE1], name: 'JPEG_EXIF' },
        // HEIC/MP4 ftyp box
        { pattern: [0x66, 0x74, 0x79, 0x70], name: 'HEIC_FTYP' },
        // Look for EXIF string
        { pattern: [0x45, 0x78, 0x69, 0x66], name: 'EXIF_STRING' },
        // Look for date patterns in HEIC files
        { pattern: [0x32, 0x30, 0x32, 0x34], name: 'DATE_2024' }, // Look for "2024" as year
        { pattern: [0x32, 0x30, 0x32, 0x33], name: 'DATE_2023' }, // Look for "2023" as year
        { pattern: [0x32, 0x30, 0x32, 0x32], name: 'DATE_2022' }, // Look for "2022" as year
        { pattern: [0x32, 0x30, 0x32, 0x31], name: 'DATE_2021' }, // Look for "2021" as year
        { pattern: [0x32, 0x30, 0x32, 0x30], name: 'DATE_2020' }, // Look for "2020" as year
    ];
    
    for (let i = 0; i < arrayBuffer.byteLength - 10; i++) {
        for (const searchPattern of searchPatterns) {
            if (matchesPattern(dataView, i, searchPattern.pattern)) {
                const extractedData = extractDataAtOffset(dataView, i, searchPattern.name);
                if (extractedData && Object.keys(extractedData).length > 0) {
                    return { ...exifData, ...extractedData };
                }
            }
        }
    }
    
    // Try to find date patterns in the buffer
    const datePattern = findDatePatternInBuffer(dataView, arrayBuffer.byteLength);
    if (datePattern) {
        return { ...exifData, ...datePattern };
    }
    
    return exifData;
};

/**
 * Look for date patterns in the buffer
 */
const findDatePatternInBuffer = (dataView: DataView, length: number): ExifData | null => {
    // Look for date patterns like "2024:01:15 12:30:45"
    for (let i = 0; i < length - 20; i++) {
        // Check for year pattern (4 digits)
        const year1 = dataView.getUint8(i);
        const year2 = dataView.getUint8(i + 1);
        const year3 = dataView.getUint8(i + 2);
        const year4 = dataView.getUint8(i + 3);
        
        if (year1 >= 0x32 && year1 <= 0x33 && // '2' or '3'
            year2 >= 0x30 && year2 <= 0x39 && // '0'-'9'
            year3 >= 0x30 && year3 <= 0x39 && // '0'-'9'
            year4 >= 0x30 && year4 <= 0x39) { // '0'-'9'
            
            // Check if followed by colon
            if (dataView.getUint8(i + 4) === 0x3A) { // ':'
                // Try to extract the full date string
                const dateString = extractDateString(dataView, i, length);
                if (dateString) {
                    return { dateTimeOriginal: dateString };
                }
            }
        }
    }
    
    return null;
};

/**
 * Extract date string from buffer starting at offset
 */
const extractDateString = (dataView: DataView, offset: number, length: number): string | null => {
    let dateStr = '';
    let i = offset;
    
    // Extract up to 19 characters (YYYY:MM:DD HH:MM:SS)
    while (i < offset + 19 && i < length) {
        const char = dataView.getUint8(i);
        
        // Check if it's a valid date character (digits, colon, space)
        if ((char >= 0x30 && char <= 0x39) || // '0'-'9'
            char === 0x3A || // ':'
            char === 0x20) { // ' '
            dateStr += String.fromCharCode(char);
        } else {
            break;
        }
        i++;
    }
    
    // Validate the date string format
    if (dateStr.length >= 19 && /^\d{4}:\d{2}:\d{2} \d{2}:\d{2}:\d{2}$/.test(dateStr)) {
        return dateStr;
    }
    
    return null;
};

/**
 * Check if a pattern matches at a specific offset
 */
const matchesPattern = (dataView: DataView, offset: number, pattern: number[]): boolean => {
    for (let i = 0; i < pattern.length; i++) {
        if (dataView.getUint8(offset + i) !== pattern[i]) {
            return false;
        }
    }
    return true;
};

/**
 * Extract EXIF data at a specific offset
 */
const extractDataAtOffset = (dataView: DataView, offset: number, type: string): ExifData => {
    const exifData: ExifData = {};
    
    if (type === 'JPEG_EXIF') {
        // Handle JPEG EXIF
        const exifStart = offset + 4;
        if (dataView.getUint32(exifStart) === 0x45786966) { // "Exif"
            const tiffStart = exifStart + 6;
            return parseTiffData(dataView, tiffStart);
        }
    } else if (type === 'EXIF_STRING') {
        // Found EXIF string, try to parse as TIFF
        const tiffStart = offset + 6;
        return parseTiffData(dataView, tiffStart);
    }
    
    return exifData;
};

/**
 * Parses EXIF data from an ArrayBuffer
 * This parser handles both JPEG and HEIC files
 */
const parseExifData = (arrayBuffer: ArrayBuffer): ExifData => {
    const dataView = new DataView(arrayBuffer);
    const exifData: ExifData = {};
    
    // Check for JPEG header
    if (dataView.getUint16(0) === 0xFFD8) {
        return parseJpegExifData(dataView, arrayBuffer.byteLength);
    }
    
    // Check for HEIC header (ftyp box)
    if (arrayBuffer.byteLength >= 12) {
        const ftypBox = new Uint8Array(arrayBuffer, 4, 4);
        const ftypString = String.fromCharCode(...ftypBox);
        
        if (ftypString === 'ftyp') {
            return parseHeicExifData(dataView, arrayBuffer.byteLength);
        }
    }
    
    return exifData;
};

/**
 * Parses EXIF data from JPEG files
 */
const parseJpegExifData = (dataView: DataView, length: number): ExifData => {
    const exifData: ExifData = {};
    let offset = 2;
    
    // Look for EXIF marker (0xFFE1)
    while (offset < length - 1) {
        const marker = dataView.getUint16(offset);
        
        if (marker === 0xFFE1) {
            // Found EXIF marker
            const exifStart = offset + 4;
            
            // Check for EXIF header
            if (dataView.getUint32(exifStart) === 0x45786966) { // "Exif"
                const tiffStart = exifStart + 6;
                const parsedData = parseTiffData(dataView, tiffStart);
                return { ...exifData, ...parsedData };
            }
        }
        
        offset += 2;
    }
    
    return exifData;
};

/**
 * Parses EXIF data from HEIC files
 * HEIC files use MP4 container format with EXIF data in 'meta' box
 */
const parseHeicExifData = (dataView: DataView, length: number): ExifData => {
    const exifData: ExifData = {};
    let offset = 0;
    
    // Parse MP4 boxes to find 'meta' box
    while (offset < length - 8) {
        const boxSize = dataView.getUint32(offset);
        const boxType = new Uint8Array(dataView.buffer, offset + 4, 4);
        const boxTypeString = String.fromCharCode(...boxType);
        
        if (boxTypeString === 'meta') {
            // Found meta box, look for EXIF data inside
            const metaStart = offset + 8;
            const metaData = parseMetaBox(dataView, metaStart, boxSize - 8);
            return { ...exifData, ...metaData };
        }
        
        if (boxSize === 0 || boxSize > length - offset) {
            break;
        }
        
        offset += boxSize;
    }
    
    return exifData;
};

/**
 * Parses the meta box in HEIC files to find EXIF data
 */
const parseMetaBox = (dataView: DataView, startOffset: number, length: number): ExifData => {
    const exifData: ExifData = {};
    let offset = startOffset;
    const endOffset = startOffset + length;
    
    // Skip version and flags (4 bytes)
    offset += 4;
    
    // Parse sub-boxes in meta box
    while (offset < endOffset - 8) {
        const boxSize = dataView.getUint32(offset);
        const boxType = new Uint8Array(dataView.buffer, offset + 4, 4);
        const boxTypeString = String.fromCharCode(...boxType);
        
        if (boxTypeString === 'iinf') {
            // Item info box - contains references to EXIF data
            const iinfData = parseIinfBox(dataView, offset + 8, boxSize - 8);
            return { ...exifData, ...iinfData };
        }
        
        if (boxSize === 0 || boxSize > endOffset - offset) {
            break;
        }
        
        offset += boxSize;
    }
    
    return exifData;
};

/**
 * Parses the iinf box to find EXIF item references
 */
const parseIinfBox = (dataView: DataView, startOffset: number, length: number): ExifData => {
    const exifData: ExifData = {};
    let offset = startOffset;
    const endOffset = startOffset + length;
    
    // Skip version and entry count
    offset += 4;
    
    // Look for EXIF item entries
    while (offset < endOffset - 8) {
        const itemId = dataView.getUint16(offset);
        const itemType = new Uint8Array(dataView.buffer, offset + 2, 4);
        const itemTypeString = String.fromCharCode(...itemType);
        
        if (itemTypeString === 'Exif') {
            // Found EXIF item, extract the data
            // This is a simplified approach - in reality, we'd need to follow
            // the item reference to get the actual EXIF data
            // For now, we'll try to extract basic date info from common locations
            const basicExif = extractBasicHeicExif(dataView, offset);
            return { ...exifData, ...basicExif };
        }
        
        offset += 6;
    }
    
    return exifData;
};

/**
 * Extracts basic EXIF data from HEIC files
 * HEIC files store EXIF data as TIFF format, so we search for TIFF headers
 * and use the existing TIFF parser to extract the data
 */
const extractBasicHeicExif = (dataView: DataView, exifItemOffset: number): ExifData => {
    const bufferLength = dataView.buffer.byteLength;
    
    // Search for TIFF headers in the buffer
    // TIFF headers are either:
    // - 0x4949 0x002A (little-endian "II*")
    // - 0x4D4D 0x002A (big-endian "MM*")
    for (let i = 0; i < bufferLength - 8; i++) {
        const byte1 = dataView.getUint16(i);
        const byte2 = dataView.getUint16(i + 2);
        
        // Check for little-endian TIFF (0x4949 "II" followed by 0x002A "*")
        if (byte1 === 0x4949 && byte2 === 0x002A) {
            try {
                const exifData = parseTiffData(dataView, i);
                if (exifData && (exifData.dateTime || exifData.dateTimeOriginal || exifData.dateTimeDigitized)) {
                    return exifData;
                }
            } catch (e) {
                // Continue searching if this wasn't valid TIFF
                continue;
            }
        }
        
        // Check for big-endian TIFF (0x4D4D "MM" followed by 0x002A "*")
        if (byte1 === 0x4D4D && byte2 === 0x002A) {
            try {
                const exifData = parseTiffData(dataView, i);
                if (exifData && (exifData.dateTime || exifData.dateTimeOriginal || exifData.dateTimeDigitized)) {
                    return exifData;
                }
            } catch (e) {
                // Continue searching if this wasn't valid TIFF
                continue;
            }
        }
    }
    
    // If no TIFF header found, try searching for EXIF string followed by TIFF
    // Some HEIC files have "Exif" string before the TIFF data
    for (let i = 0; i < bufferLength - 12; i++) {
        const exifString = String.fromCharCode(
            dataView.getUint8(i),
            dataView.getUint8(i + 1),
            dataView.getUint8(i + 2),
            dataView.getUint8(i + 3)
        );
        
        if (exifString === 'Exif') {
            // Found "Exif" string, check if followed by TIFF header
            const tiffOffset = i + 6; // Skip "Exif" (4 bytes) + padding (2 bytes)
            if (tiffOffset < bufferLength - 4) {
                const byte1 = dataView.getUint16(tiffOffset);
                const byte2 = dataView.getUint16(tiffOffset + 2);
                
                if ((byte1 === 0x4949 || byte1 === 0x4D4D) && byte2 === 0x002A) {
                    try {
                        const exifData = parseTiffData(dataView, tiffOffset);
                        if (exifData && (exifData.dateTime || exifData.dateTimeOriginal || exifData.dateTimeDigitized)) {
                            return exifData;
                        }
                    } catch (e) {
                        // Continue searching
                        continue;
                    }
                }
            }
        }
    }
    
    return {};
};

/**
 * Parses TIFF data structure within EXIF
 */
const parseTiffData = (dataView: DataView, startOffset: number): ExifData => {
    const exifData: ExifData = {};
    
    try {
        // Check byte order
        const byteOrder = dataView.getUint16(startOffset);
        const isLittleEndian = byteOrder === 0x4949; // "II"
        
        const getUint16 = (offset: number) => isLittleEndian ? 
            dataView.getUint16(offset, true) : dataView.getUint16(offset, false);
        const getUint32 = (offset: number) => isLittleEndian ? 
            dataView.getUint32(offset, true) : dataView.getUint32(offset, false);
        
        // Get IFD offset
        const ifdOffset = getUint32(startOffset + 4);
        const ifdStart = startOffset + ifdOffset;
        
        // Validate IFD start is within bounds
        if (ifdStart >= dataView.buffer.byteLength || ifdStart < startOffset) {
            return exifData;
        }
        
        // Get number of directory entries
        const numEntries = getUint16(ifdStart);
        
        // Parse directory entries
        for (let i = 0; i < numEntries; i++) {
            const entryOffset = ifdStart + 2 + (i * 12);
            if (entryOffset + 12 > dataView.buffer.byteLength) {
                break;
            }
            
            const tag = getUint16(entryOffset);
            const type = getUint16(entryOffset + 2);
            const count = getUint32(entryOffset + 4);
            const valueOffset = entryOffset + 8;
            
            // Extract date fields
            if (tag === 0x0132 && type === 2) { // DateTime
                exifData.dateTime = readExifString(dataView, valueOffset, count, isLittleEndian, startOffset);
            } else if (tag === 0x9003 && type === 2) { // DateTimeOriginal
                exifData.dateTimeOriginal = readExifString(dataView, valueOffset, count, isLittleEndian, startOffset);
            } else if (tag === 0x9004 && type === 2) { // DateTimeDigitized
                exifData.dateTimeDigitized = readExifString(dataView, valueOffset, count, isLittleEndian, startOffset);
            } else if (tag === 0x010F && type === 2) { // Make
                exifData.make = readExifString(dataView, valueOffset, count, isLittleEndian, startOffset);
            } else if (tag === 0x0110 && type === 2) { // Model
                exifData.model = readExifString(dataView, valueOffset, count, isLittleEndian, startOffset);
            } else if (tag === 0x0131 && type === 2) { // Software
                exifData.software = readExifString(dataView, valueOffset, count, isLittleEndian, startOffset);
            } else if (tag === 0x0112 && type === 3) { // Orientation
                exifData.orientation = getUint16(valueOffset);
            }
        }
    } catch (error) {
        // Silently return empty data on error
    }
    
    return exifData;
};

/**
 * Reads an EXIF string from the data view, handling both inline and offset values
 */
const readExifString = (dataView: DataView, valueOffset: number, count: number, isLittleEndian: boolean, tiffStart: number): string => {
    let stringOffset: number;
    
    // If the string is 4 bytes or less, it's stored inline
    if (count <= 4) {
        stringOffset = valueOffset;
    } else {
        // Otherwise, the value field contains an offset to the actual string
        const getUint32 = (offset: number) => isLittleEndian ? 
            dataView.getUint32(offset, true) : dataView.getUint32(offset, false);
        stringOffset = tiffStart + getUint32(valueOffset);
    }
    
    let result = '';
    for (let i = 0; i < count - 1; i++) { // -1 to exclude null terminator
        const charCode = dataView.getUint8(stringOffset + i);
        if (charCode === 0) break;
        result += String.fromCharCode(charCode);
    }
    return result;
};

/**
 * Converts EXIF date string to YYYY-MM-DD hh:mm:ss format
 * EXIF dates are typically in format "YYYY:MM:DD HH:MM:SS"
 */
export const parseExifDate = (exifDateString: string): string | null => {
    if (!exifDateString) return null;
    
    try {
        // EXIF date format: "YYYY:MM:DD HH:MM:SS"
        const dateStr = exifDateString.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3'); // Replace first two colons with dashes
        const date = new Date(dateStr);
        
        if (isNaN(date.getTime())) {
            return null;
        }
        
        // Format as YYYY-MM-DD hh:mm:ss
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
        console.warn('Failed to parse EXIF date:', exifDateString, error);
        return null;
    }
};

/**
 * Gets the best available date from EXIF data
 */
export const getBestExifDate = (exifData: ExifData): string | null => {
    // Priority order: DateTimeOriginal > DateTimeDigitized > DateTime
    if (exifData.dateTimeOriginal) {
        return parseExifDate(exifData.dateTimeOriginal);
    }
    if (exifData.dateTimeDigitized) {
        return parseExifDate(exifData.dateTimeDigitized);
    }
    if (exifData.dateTime) {
        return parseExifDate(exifData.dateTime);
    }
    return null;
};
